import React, {useEffect, useLayoutEffect, useRef, useState} from 'react';
import {useDispatch} from 'react-redux';

import {logout} from '@core/actions/UserActions';

import useClickOutside from '../../hooks/useClickOutside';
import useExternalNavigation from '../../hooks/useExternalNavigation';
import useHeaderData from '../../hooks/useHeaderData';
import AddFundsModal from '../AddFundsModal/AddFundsModal';
import BusyOverlay from '../BusyOverlay/BusyOverlay';
import FieldIcon from '../FieldIcon';
import PendingOrderNudge from '../PendingOrderNudge';

// Authenticated-customer header — single source of truth across bidding bundles
// (account, bidding-flow, and any future authenticated page).
//
// Design rule: markup + class names mirror the tutoriage prod template exactly.
// Satellite legacy CSS (header.css, cabinetCommon.css, main.min.css) paints all
// of it; the core ships zero styles for this component. Every brand can swap
// logo / colors / typography from their own CSS without touching this tree.
//
// Per-brand URL differences (e.g. `/my-orders` legacy vs `/account` canonical)
// resolve to the new customer's SPA paths — the core is the new source of
// truth. Satellites that want legacy paths redirect from the satellite side.
export default function Header({
                                   links = DEFAULT_LINKS,
                                   nav = [],
                                   variant,
                                   onAddFunds,
                               }) {
    const dispatch = useDispatch();
    const [menuOpen, setMenuOpen] = useState(false);
    const [loggingOut, setLoggingOut] = useState(false);
    // Header nav links (My orders / My balance / New Order, etc.) point at
    // server-rendered routes that live outside the current bundle — clicking
    // them triggers a full page reload, which leaves the browser white until
    // the next paint. The hook pre-paints `BusyOverlay` over that gap so the
    // click never feels dead. Mirrors the auth flow's "Signing you in" UX.
    const { navigatingMessage, navigate: navigateExternal } = useExternalNavigation();

    // Intercept a header link's click and run it through the overlay-then-navigate
    // path. Modifier-clicks (cmd/ctrl/middle/shift) pass through to the native
    // `<a href>` so "open in new tab" still works; the overlay fires only for
    // plain primary-button clicks.
    const navOnClick = (href) => (e) => {
        if (!href) return;
        if (e.defaultPrevented) return;
        if (e.button !== 0) return;
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
        e.preventDefault();
        setMenuOpen(false);
        navigateExternal(href);
    };
    // Header owns the Add Funds modal so EVERY page that mounts a
    // Header gets the behavior for free — no per-page wiring, no
    // duplicated state. Pages that want to hijack the flow (e.g. open
    // their own custom modal) can pass an `onAddFunds` prop; if it
    // does anything (returns `true` / calls `e.preventDefault()`), the
    // built-in modal stays closed.
    const [addFundsOpen, setAddFundsOpen] = useState(false);
    const menuRef = useRef(null);
    const triggerRef = useRef(null);
    const mobileTriggerRef = useRef(null);
    const burgerTriggerRef = useRef(null);

    useClickOutside(
        [menuRef, triggerRef, mobileTriggerRef, burgerTriggerRef],
        () => setMenuOpen(false),
        menuOpen,
    );

    // Anchor the dropdown directly under whichever trigger is on screen
    // (desktop account icon vs mobile burger / profile). Legacy CSS
    // anchors via a viewport-width formula that breaks on dev brand
    // widths — getBoundingClientRect is reliable everywhere.
    const [menuPos, setMenuPos] = useState(null);
    useLayoutEffect(() => {
        if (!menuOpen) return undefined;

        const reposition = () => {
            const trigger = triggerRef.current?.offsetParent
                ? triggerRef.current
                : (mobileTriggerRef.current || burgerTriggerRef.current);
            if (!trigger) return;
            const rect = trigger.getBoundingClientRect();
            // Right-align the menu's right edge with the trigger's right edge.
            // 8px gap below the trigger so the arrow sits above the bubble.
            setMenuPos({
                top: Math.round(rect.bottom + 8),
                right: Math.max(8, Math.round(window.innerWidth - rect.right)),
            });
        };

        reposition();
        window.addEventListener('resize', reposition);
        window.addEventListener('scroll', reposition, true);
        return () => {
            window.removeEventListener('resize', reposition);
            window.removeEventListener('scroll', reposition, true);
        };
    }, [menuOpen]);

    const {authorized, balanceAmount, referralEnabled} = useHeaderData();

    const triggerClass = `bidding-header-mobile__account popup${menuOpen ? ' active' : ''}`;
    const menuClass = `bidding-account-menu popup${menuOpen ? ' active' : ''}`;

    // Prevent default so link-style triggers don't navigate; preserve
    // `.js-add-founds` / popup classes so legacy brand CSS keeps hooking.
    const onTrigger = (e) => {
        e.preventDefault();
        setMenuOpen(o => !o);
    };
    const onAdd = (e) => {
        e.preventDefault();
        setMenuOpen(false);
        // Caller override takes priority — prop wins if supplied. Every
        // other page gets the built-in modal for free.
        if (typeof onAddFunds === 'function') {
            onAddFunds(e);
            return;
        }
        setAddFundsOpen(true);
    };

    // POST AJAX to /api/user/logout, then hard-reload to '/' so PHP session
    // on the satellite / customer nginx side also drops. Mirrors v5 pattern.
    // Overlay stays on screen until the redirect — no flicker of logged-in
    // chrome between "Logout" click and the new page.
    const onLogout = (e) => {
        e.preventDefault();
        if (loggingOut) return;
        setMenuOpen(false);
        setLoggingOut(true);
        Promise.resolve(dispatch(logout())).finally(() => {
            window.location.href = '/';
        });
    };

    // `page-header` + `user-is-auth` = root classes expected by tutoriage /
    // bidforwriting / essaywritery legacy CSS (`.page-header .wrap div ul li…`
    // in main.min.css). Without them layout rules don't scope in and flex /
    // gap / positioning collapse.
    // Home "hero" variant: header sits transparent over the blue hero with a
    // white logo + white nav, then swaps to the solid-white chrome once the
    // visitor scrolls past the hero band. Mirrors the legacy `home-header` →
    // `home-header.fixed` toggle. Other mounts (account, /writer, inner brand
    // pages) leave `variant` unset and render the solid header as-is.
    const isHero = variant === 'hero';
    const [scrolled, setScrolled] = useState(false);
    useEffect(() => {
        if (!isHero) return undefined;
        const onScroll = () => setScrolled((window.scrollY || window.pageYOffset || 0) > 60);
        onScroll();
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => window.removeEventListener('scroll', onScroll);
    }, [isHero]);

    const headerClass = [
        'bidding-page-header',
        authorized && 'user-is-auth',
        isHero && 'bidding-page-header--hero',
        isHero && scrolled && 'is-scrolled',
    ].filter(Boolean).join(' ');

    // "New Order" / "Sign Up" CTA: guests land on the register view first
    // (the wizard create-order POST 403s for guests anyway), authed users go
    // straight to the wizard. Mirrors the PHP `newOrderUrl()` helper so the
    // React island and any server-rendered fallback agree.
    const newOrderHref = authorized ? links.newOrder : links.register;

    // Marketing nav (How it works / Top writers / About us) is shown to
    // everyone; it's the public site nav the brand .phtml headers carried.
    const navList = nav;

    return (
        <>
        <header className={headerClass}>
            <div className="wrap">
                <a href={links.home} className="bidding-header-logo" aria-label="Home"/>

                <div className="bidding-header-desc">
                    {/* Marketing nav is for guests only — authed users get the
                        account chrome (My orders / balance / …) instead, matching
                        the legacy header. */}
                    {!authorized && navList.length > 0 && (
                        <ul className="bidding-header-nav">
                            {navList.map((item) => (
                                <li key={item.href}>
                                    {/* Plain links — marketing pages don't need the
                                        "Opening your order" busy overlay (wrong copy
                                        for them, and it's the bfcache stuck-preloader
                                        source). Native nav is instant enough. */}
                                    <a href={item.href}>{item.label}</a>
                                </li>
                            ))}
                        </ul>
                    )}
                    <ul className="bidding-header-menu">
                        {authorized ? (
                            <>
                                <li>
                                    <a
                                        id="only-autotest-button-my-orders"
                                        href={links.orders}
                                        onClick={navOnClick(links.orders)}
                                    >
                                        <FieldIcon name="package" size={16} className="bidding-header-menu__icon" />
                                        <span>My orders</span>
                                    </a>
                                </li>
                                <li>
                                    <a
                                        id="only-autotest-button-my-balance"
                                        className="bidding-balance-link"
                                        href={links.balance}
                                        onClick={navOnClick(links.balance)}
                                    >
                                        <FieldIcon name="wallet" size={16} className="bidding-header-menu__icon" />
                                        <span>My balance</span>
                                        <span className="bidding-head-balance">{balanceAmount}</span>
                                    </a>
                                </li>
                                {/* Account dropdown stays where it's always been —
                                    between balance and the CTA pair. */}
                                <li className="bidding-header-menu_right-side">
                                    <a
                                        ref={triggerRef}
                                        id="only-autotest-button-pop-up-account"
                                        href="#"
                                        className={triggerClass}
                                        onClick={onTrigger}
                                        aria-haspopup="true"
                                        aria-expanded={menuOpen}
                                    >
                                        <AccountIcon/>
                                    </a>
                                </li>
                                <li className="bidding-header-funds">
                                    <a
                                        id="only-autotest-button-add-funds"
                                        href="#"
                                        className="js-add-founds"
                                        onClick={onAdd}
                                    >
                                        <AddIcon/>
                                        Add Funds
                                    </a>
                                </li>
                            </>
                        ) : (
                            <li className="bidding-header-login">
                                <a
                                    id="only-autotest-button-login"
                                    href={links.login}
                                    onClick={navOnClick(links.login)}
                                >
                                    Log In
                                </a>
                            </li>
                        )}
                        <li className="bidding-order-now">
                            <a
                                id="only-autotest-button-new-order"
                                href={newOrderHref}
                                onClick={navOnClick(newOrderHref)}
                            >
                                New Order
                            </a>
                        </li>
                    </ul>
                </div>

                <div className="bidding-header-mobile">
                    {/* One trigger per auth state (no redundant double icon):
                        authed → account person icon (opens the account menu);
                        guest  → hamburger (opens the nav + Log In menu). */}
                    {authorized ? (
                        <a
                            ref={mobileTriggerRef}
                            href="#"
                            className="bidding-header-mobile__account"
                            onClick={onTrigger}
                            aria-haspopup="true"
                            aria-expanded={menuOpen}
                        >
                            <AccountIcon/>
                        </a>
                    ) : (
                        <div
                            ref={burgerTriggerRef}
                            className="bidding-humburger"
                            onClick={onTrigger}
                            role="button"
                            aria-label="Menu"
                            aria-haspopup="true"
                            aria-expanded={menuOpen}
                        >
                            <HamburgerIcon/>
                        </div>
                    )}
                    <a
                        href={newOrderHref}
                        className="bidding-header-order__link"
                        onClick={navOnClick(newOrderHref)}
                    >
                        Order
                    </a>
                </div>
            </div>

            {/*
              .account_menu lives OUTSIDE .wrap so it does not participate in
              .wrap's flexbox as a 3rd child (even though it's position:fixed).
              Mirrors tutoriage prod sections/header.php structure.
            */}
            {authorized && (
                <div
                    ref={menuRef}
                    className={menuClass}
                    // Inline position stays applied AFTER close too, so
                    // the bubble fades out in place. Removing the style
                    // when menuOpen flips to false would let the legacy
                    // CSS reposition-then-fade — the customer sees the
                    // bubble jump to a different spot before disappearing.
                    style={menuPos ? {
                        position: 'fixed',
                        top: menuPos.top,
                        right: menuPos.right,
                        left: 'auto',
                        bottom: 'auto',
                    } : undefined}
                >
                    <div className="bidding-header-item active">
                        <ul className="bidding-cabinet-head-list">
                            <li>
                                <a
                                    id="only-autotest-button-profile-settings"
                                    className="bidding-orders-link"
                                    href={links.profile}
                                    onClick={navOnClick(links.profile)}
                                >
                                    <FieldIcon name="user" size={16} className="bidding-orders-link__icon"/>
                                    <span>Profile settings</span>
                                </a>
                            </li>
                            {referralEnabled && (
                                <li>
                                    <a
                                        id="only-autotest-button-referral"
                                        className="bidding-orders-link"
                                        href={links.referral}
                                        onClick={navOnClick(links.referral)}
                                    >
                                        <FieldIcon name="gift" size={16} className="bidding-orders-link__icon"/>
                                        <span>Refer a friend</span>
                                    </a>
                                </li>
                            )}
                            <li>
                                <a
                                    id="only-autotest-button-loyalty"
                                    className="bidding-orders-link"
                                    href={links.loyalty}
                                    onClick={navOnClick(links.loyalty)}
                                >
                                    <FieldIcon name="tag" size={16} className="bidding-orders-link__icon"/>
                                    <span>Loyalty program</span>
                                </a>
                            </li>
                            <li>
                                <a
                                    id="only-autotest-button-help"
                                    className="bidding-orders-link"
                                    href={links.support}
                                    onClick={navOnClick(links.support)}
                                >
                                    <FieldIcon name="helpCircle" size={16} className="bidding-orders-link__icon"/>
                                    <span>Help</span>
                                </a>
                            </li>
                            <li>
                                <a
                                    id="only-autotest-button-logout"
                                    href={links.logout}
                                    className="bidding-orders-link"
                                    onClick={onLogout}
                                >
                                    <FieldIcon name="logOut" size={16} className="bidding-orders-link__icon"/>
                                    <span>Logout</span>
                                </a>
                            </li>
                        </ul>
                    </div>
                </div>
            )}

            {/*
              Guest dropdown — what the mobile burger opens for logged-out
              visitors: the marketing nav + Log In (the desktop row hides
              behind the burger on narrow widths). Same bubble chrome /
              positioning as the authed account menu.
            */}
            {!authorized && (
                <div
                    ref={menuRef}
                    className={menuClass}
                    style={menuPos ? {
                        position: 'fixed',
                        top: menuPos.top,
                        right: menuPos.right,
                        left: 'auto',
                        bottom: 'auto',
                    } : undefined}
                >
                    <div className="bidding-header-item active">
                        <ul className="bidding-cabinet-head-list">
                            {navList.map((item) => (
                                <li key={item.href}>
                                    {/* Plain link — no busy overlay for marketing pages. */}
                                    <a className="bidding-orders-link" href={item.href}>
                                        <span>{item.label}</span>
                                    </a>
                                </li>
                            ))}
                            <li>
                                <a className="bidding-orders-link" href={links.login}>
                                    <FieldIcon name="user" size={16} className="bidding-orders-link__icon"/>
                                    <span>Log In</span>
                                </a>
                            </li>
                        </ul>
                    </div>
                </div>
            )}

            {/*
              Built-in Add Funds modal — only renders when open so the
              pay-methods fetch doesn't fire on every page load. Shared
              component across every bidding surface.
            */}
            <AddFundsModal
                open={addFundsOpen}
                onClose={() => setAddFundsOpen(false)}
            />

            {loggingOut && <BusyOverlay message="Signing you out…"/>}
            {/* External nav (My orders / My balance / New Order, etc.) —
                pre-paints over the white network gap to legacy server-
                rendered routes. `loggingOut` wins if both fire because it
                renders first; only one BusyOverlay can be Top-Layer at a
                time. */}
            {!loggingOut && navigatingMessage && (
                <BusyOverlay message={navigatingMessage}/>
            )}
        </header>
        {/*
          "Finish your order" banner. Renders outside the `<header>` tag
          so the banner can sit below the fixed-height top bar without
          fighting the header's own box-shadow / flex rules. The nudge
          itself decides whether to show based on current pathname —
          Header just gives it one mount-point that every authenticated
          page inherits for free.
        */}
        <PendingOrderNudge authorized={authorized}/>
        </>
    );
}

const DEFAULT_LINKS = {
    home: '/',
    newOrder: '/order',
    register: '/customer/?view=register',
    login: '/customer/',
    orders: '/account',
    balance: '/account/balance',
    profile: '/account/profile',
    support: '/account/support',
    referral: '/account/refer-a-friend',
    loyalty:  '/account/loyalty-program',
    logout: '/user/logout',
};

// Header SVG icons. `AccountIcon` uses Material `account_circle` — cleaner
// than the legacy `#gf7wa` path (circle + face silhouette) and reads as
// a modern user avatar without the "floating-above-horizon" artifact of
// the prior design. Fill inherits via `currentColor` in SCSS.
function AccountIcon() {
    return (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path
                d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z"/>
        </svg>
    );
}

function AddIcon() {
    return (
        <svg version="1.1" xmlns="http://www.w3.org/2000/svg" xmlnsXlink="http://www.w3.org/1999/xlink" width="13"
             height="13" viewBox="0 0 13 13">
            <g>
                <path id="icon_add"
                      d="M10.251,6.924 C10.251,7.198 10.025,7.424 9.751,7.424 C9.751,7.424 7.751,7.424 7.751,7.424 C7.751,7.424 7.751,9.424 7.751,9.424 C7.751,9.698 7.525,9.924 7.251,9.924 C7.251,9.924 6.251,9.924 6.251,9.924 C5.978,9.924 5.751,9.698 5.751,9.424 C5.751,9.424 5.751,7.424 5.751,7.424 C5.751,7.424 3.751,7.424 3.751,7.424 C3.478,7.424 3.251,7.198 3.251,6.924 C3.251,6.924 3.251,5.924 3.251,5.924 C3.251,5.651 3.478,5.424 3.751,5.424 C3.751,5.424 5.751,5.424 5.751,5.424 C5.751,5.424 5.751,3.424 5.751,3.424 C5.751,3.151 5.978,2.924 6.251,2.924 C6.251,2.924 7.251,2.924 7.251,2.924 C7.525,2.924 7.751,3.151 7.751,3.424 C7.751,3.424 7.751,5.424 7.751,5.424 C7.751,5.424 9.751,5.424 9.751,5.424 C10.025,5.424 10.251,5.651 10.251,5.924 C10.251,5.924 10.251,6.924 10.251,6.924 zM12.751,6.424 C12.751,3.112 10.064,0.424 6.751,0.424 C3.439,0.424 0.751,3.112 0.751,6.424 C0.751,9.737 3.439,12.424 6.751,12.424 C10.064,12.424 12.751,9.737 12.751,6.424 z"/>
            </g>
        </svg>
    );
}

function HamburgerIcon() {
    return (
        <svg xmlns="http://www.w3.org/2000/svg" xmlnsXlink="http://www.w3.org/1999/xlink" width="23" height="16"
             viewBox="0 0 23 16">
            <defs>
                <path id="sa15a"
                      d="M318.89 16h21.25a.89.89 0 0 1 0 1.78h-21.25a.89.89 0 0 1 0-1.78zm.01 7.09h21.2a.9.9 0 1 1 0 1.81h-21.2a.9.9 0 1 1 0-1.81zm-.04 7.13h21.25a.89.89 0 0 1 0 1.78h-21.25a.89.89 0 0 1 0-1.78z"/>
            </defs>
            <g>
                <g transform="translate(-318 -16)">
                    <use xlinkHref="#sa15a"/>
                </g>
            </g>
        </svg>
    );
}
